import { useCallback, useMemo } from 'react';
import type {
  OperationDto,
  OperationStatusIcon,
  OperationTypeIcon,
} from '@/shared/types';
import { LabeledIcon } from '@/shared/components';
import { capitalize } from '@/shared/utils';
import { formatInTimeZone } from 'date-fns-tz';
import { formatReceivedAmount, useDateLocale } from '@/shared/lib';
import {
  DATE_TIME_DISPLAY_FORMAT,
  HIDDEN_BALANCES_FULL_TEXT,
} from '@/shared/config';

type Props = {
  isBalancesHidden?: boolean;
  operation: OperationDto;
  operationStatusIcons: Array<OperationStatusIcon>;
  operationTypeIcons: Array<OperationTypeIcon>;
  onOperationItemClick?: (operation: OperationDto) => void;
  preferAdditionalAmount?: boolean;
};

export const OperationsHistoryItem = ({
  isBalancesHidden,
  operation,
  operationStatusIcons,
  operationTypeIcons,
  onOperationItemClick,
  preferAdditionalAmount = false,
}: Props) => {
  const { dateLocale } = useDateLocale();

  const operationStatusIcon = operationStatusIcons.find(
    icon => icon.status === operation.status
  );
  const operationTypeIcon = operationTypeIcons.find(
    icon => icon.operationType === operation.operationType
  );

  const operationDate = useMemo(
    () =>
      formatInTimeZone(operation.date, 'UTC', DATE_TIME_DISPLAY_FORMAT, {
        locale: dateLocale,
      }),
    [operation.date, dateLocale]
  );

  const handleOperationItemClick = useCallback(() => {
    onOperationItemClick?.(operation);
  }, [operation, onOperationItemClick]);

  const amountRows = useMemo(() => {
    const main = {
      direction: operation.direction,
      amount: formatReceivedAmount(operation.amount),
      code: operation.asset.code,
      isAdditional: false,
    };

    if (
      operation.additionalAmount &&
      operation.additionalAsset &&
      operation.additionalDirection
    ) {
      const additional = {
        direction: operation.additionalDirection,
        amount: formatReceivedAmount(operation.additionalAmount),
        code: operation.additionalAsset.code,
        isAdditional: true,
      };

      return preferAdditionalAmount ? [additional, main] : [main, additional];
    }

    return [main];
  }, [operation, preferAdditionalAmount]);

  return (
    <div
      className="flex items-center gap-[1.6rem] py-[1.4rem] group hover:bg-secondary-bg cursor-pointer rounded-4xl px-0 md:px-[1.6rem] w-full"
      onClick={handleOperationItemClick}>
      {operationTypeIcon?.icon && operationStatusIcon?.icon && (
        <LabeledIcon
          icon={operationTypeIcon?.icon}
          label={operationStatusIcon?.icon}
          // Temp solution to provide proper visibility in dark mode for icons from API
          className="border-none bg-secondary-bg group-hover:bg-stroke w-[4.4rem] h-[4.4rem]"
          labelClassName="border-[0.2rem] w-[1.8rem] h-[1.8rem] border-white group-hover:border-secondary-bg rounded-full"
        />
      )}
      <div className="grow flex flex-col gap-[0.2rem]">
        <div className="flex items-center justify-between">
          <h4 className="medium4 text-primary">
            {operation.operationSubTypeName}
          </h4>

          <div className="medium4 text-primary text-end">
            {isBalancesHidden ? (
              HIDDEN_BALANCES_FULL_TEXT
            ) : (
              <>
                {amountRows[0].direction === 'credit' ? '+' : '-'}{' '}
                {amountRows[0].amount}
              </>
            )}{' '}
            {amountRows[0].code}
          </div>
        </div>
        <div className="flex items-center justify-between">
          <p className="p5 text-secondary">{capitalize(operationDate)}</p>
          {amountRows[1] && (
            <div className="p5 text-secondary text-end whitespace-nowrap">
              {isBalancesHidden ? (
                HIDDEN_BALANCES_FULL_TEXT
              ) : (
                <>
                  {amountRows[1].direction === 'credit' ? '+' : '-'}{' '}
                  {amountRows[1].amount}
                </>
              )}{' '}
              {amountRows[1].code}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
